import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Mail } from "lucide-react";

export interface SentInviteChip {
  email: string;
  roleLabel: string;
}

interface Step7TeamProps {
  email: string;
  inviteRole: "cleaner" | "company_admin";
  sentInvites: SentInviteChip[];
  onEmailChange: (email: string) => void;
  onRoleChange: (role: "cleaner" | "company_admin") => void;
  onSendInvite: () => Promise<void>;
  loading?: boolean;
}

export function Step7Team({
  email,
  inviteRole,
  sentInvites,
  onEmailChange,
  onRoleChange,
  onSendInvite,
  loading = false,
}: Step7TeamProps) {
  const [submitting, setSubmitting] = useState(false);

  const handleSend = async () => {
    setSubmitting(true);
    try {
      await onSendInvite();
    } finally {
      setSubmitting(false);
    }
  };

  const busy = loading || submitting;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Invite your team</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Send invites to cleaners or your cleaning company. They will receive an email to join this
          property.
        </p>
      </div>

      <div className="space-y-4 rounded-lg border p-4">
        <div className="space-y-2">
          <Label htmlFor="invite-email">Email</Label>
          <Input
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            placeholder="cleaner@example.com"
            autoComplete="email"
          />
        </div>
        <div className="space-y-2">
          <Label>Role</Label>
          <Select
            value={inviteRole}
            onValueChange={(v) => onRoleChange(v as "cleaner" | "company_admin")}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cleaner">Cleaner</SelectItem>
              <SelectItem value="company_admin">Cleaning Company</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          type="button"
          onClick={handleSend}
          disabled={busy || !email.trim()}
          className="w-full sm:w-auto"
        >
          {busy ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Sending…
            </>
          ) : (
            <>
              <Mail className="h-4 w-4 mr-2" />
              Send Invite
            </>
          )}
        </Button>
      </div>

      {sentInvites.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {sentInvites.map((inv) => (
            <span
              key={inv.email}
              className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-sm"
            >
              <span className="truncate max-w-[12rem]">{inv.email}</span>
              <span className="text-muted-foreground text-xs">({inv.roleLabel})</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
